import { useState, useCallback, useRef, useEffect } from 'react';
import {
  sortResultCache,
  filterResultCache,
  clearResultCacheSort,
  clearResultCacheFilter,
  exportCacheToCsv,
  cancelCsvExport,
  onCsvExportProgress,
} from '../services/resultCacheApi';
// TODO: consider a path alias (e.g., @/components/ui/toaster) to shorten this deep relative import
import { toast } from '../../../../components/ui/toaster';
import type { ResultCacheCapabilities, SkipCauseClass } from '../../../../types/queryExecution';
import {
  showExportSuccessToast,
  showExportPartialKeptToast,
  showExportSkippedRowsToast,
} from '../utils/exportSuccessToast';
import {
  describeExportProgress,
  describeExportRecovery,
  describeExportRetry,
  describeExportStall,
} from '../utils/csvExportToastText';
import { classifyExportFailure, bucketRowCount, bucketSkippedRows } from '../utils/csvExportTelemetry';
import type { GridExportOutcome } from '../store/gridExportRegistry';

/**
 * Combine the skip-cause classes of a resume chain: agreement keeps the
 * class, disagreement is 'mixed'. Exported for tests.
 */
export function mergeSkipCauseClass(
  carried: SkipCauseClass | undefined,
  current: SkipCauseClass | undefined,
): SkipCauseClass | undefined {
  if (carried == null) return current;
  if (current == null) return carried;
  return carried === current ? carried : 'mixed';
}

/**
 * Union of the skips a previous partial run carried over and the current
 * run's skips, sorted and de-duplicated — the complete list of rows missing
 * from the file across a resume chain. Exported for tests.
 */
export function mergeSkippedRowIndexes(
  carried: readonly number[] | undefined,
  current: readonly number[] | undefined,
): number[] {
  if (!carried?.length) return current ? [...current].sort((a, b) => a - b) : [];
  return [...new Set([...carried, ...(current ?? [])])].sort((a, b) => a - b);
}

/** Shallow-compares two string-keyed record objects (one level deep). */
function shallowEqualFilters(a: Record<string, string>, b: Record<string, string>): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

type UseWindowedGridParams = {
  isWindowed: boolean;
  cacheUri: string | undefined;
  remoteTotalRowCount: number | undefined;
  cacheGeneration: number | undefined;
  columnKeys: string[];
  showResultFilter: boolean;
  columnFilters: Record<string, string>;
  globalFilter: string;
  capabilities: ResultCacheCapabilities | undefined;
  /** T5 — native-spatial column ids present in this result set (WKT export parity). */
  nativeSpatialColumnIds?: ReadonlySet<string>;
  /** T5 — engine id, required alongside `nativeSpatialColumnIds` to decode them on export. */
  engine?: string;
  /**
   * Whether any column type can hold very large values (nvarchar(max), blob,
   * json, …) — export-failure telemetry only, see csvExportTelemetry.ts.
   */
  hasWideValueColumns?: boolean;
  /**
   * Jumps the grid to an absolute 0-BASED row index and marks it. Supplied by
   * the grid that owns the scroll viewport (ResultsGrid); surfaces without one
   * omit it and the skipped-rows toast simply keeps its file affordance.
   */
  scrollToRow?: (rowIndex0: number) => void;
  /**
   * An export finished successfully but skipped unreadable rows (S2) — fired
   * with their 0-based absolute indexes so the owning surface can raise its
   * durable "Skipped rows" tab (see `SkippedRowsPanel.tsx`). Only fired while
   * the exported result set is still the one on screen: the indexes die with
   * the buffered set, so a completion that lands after a result-set switch or
   * unmount stays a toast-only report.
   */
  onExportSkippedRows?: (skippedRows: number[]) => void;
  /**
   * "Review skipped rows" clicked on the several-rows-skipped toast — the
   * owning surface activates its "Skipped rows" tab. Omitted by surfaces
   * without one (the toast then keeps the single Show-in-folder shape).
   */
  onReviewSkippedRows?: () => void;
};

type UseWindowedGridResult = {
  windowedTotalRowCount: number;
  effectiveTotalRowCount: number;
  dataVersion: number;
  isRemoteSorting: boolean;
  handleWindowedSortChange: (newSortState: { columnId: string; direction: 'asc' | 'desc' } | null) => void;
  /**
   * Returns the outcome as well as toasting it — see the same note on
   * `useExportCopy.handleExport`. The toolbar ignores it; `ui_export_results`
   * reports it back to the agent.
   */
  handleExportAllCsv: () => Promise<GridExportOutcome>;
  isExporting: boolean;
  capabilities: ResultCacheCapabilities | undefined;
  sourceGeneration: number;
};

/**
 * Encapsulates windowed-grid state: remote sort/filter dispatch, streaming CSV export,
 * windowed total row count tracking, and dataVersion for buffer invalidation.
 */
export function useWindowedGrid({
  isWindowed,
  cacheUri,
  remoteTotalRowCount,
  cacheGeneration,
  columnKeys,
  showResultFilter,
  columnFilters,
  globalFilter,
  capabilities,
  nativeSpatialColumnIds,
  engine,
  hasWideValueColumns,
  scrollToRow,
  onExportSkippedRows,
  onReviewSkippedRows,
}: UseWindowedGridParams): UseWindowedGridResult {
  const [windowedTotalRowCount, setWindowedTotalRowCount] = useState(remoteTotalRowCount ?? 0);
  const [trackedCacheUri, setTrackedCacheUri] = useState(cacheUri);
  // Sync state to props during render when cacheUri changes (result-set tab switch).
  // Without this, windowedTotalRowCount carries stale rowCount from the previous result
  // set into the next render, leaving useWindowedData fetching the wrong row count.
  if (trackedCacheUri !== cacheUri) {
    setTrackedCacheUri(cacheUri);
    setWindowedTotalRowCount(remoteTotalRowCount ?? 0);
  }
  const [dataVersion, setDataVersion] = useState(0);
  // Covers both sort and filter remote operations — consider renaming to `isRemoteBusy`
  const [isRemoteSorting, setIsRemoteSorting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sourceGeneration, setSourceGeneration] = useState(0);
  const sourceGenerationRef = useRef(0);

  // Ref to detect stale sort/filter responses after re-execution
  const cacheGenerationRef = useRef(cacheGeneration ?? 0);
  cacheGenerationRef.current = cacheGeneration ?? 0;
  // Ref to detect stale sort/filter responses after source URI change (result set switch)
  const cacheUriRef = useRef(cacheUri);
  cacheUriRef.current = cacheUri;
  // Ref-backed so a toast action clicked minutes after the export never calls a
  // stale implementation (same reason as `runCsvExportRef` below).
  const scrollToRowRef = useRef(scrollToRow);
  scrollToRowRef.current = scrollToRow;
  const onExportSkippedRowsRef = useRef(onExportSkippedRows);
  onExportSkippedRowsRef.current = onExportSkippedRows;
  const onReviewSkippedRowsRef = useRef(onReviewSkippedRows);
  onReviewSkippedRowsRef.current = onReviewSkippedRows;
  // A persistent toast outlives the grid that raised it — a closed tab leaves
  // the "Show row" action pointing at a viewport that is no longer on screen.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  // Client-side token for sort/filter serialization — only the latest dispatch's response is accepted
  const sortFilterTokenRef = useRef(0);
  // Single-flight guard for filter dispatch: user typing fires a change per keystroke, but
  // we must only have ONE filter IPC in flight at a time. Otherwise rapid keystrokes cause
  // request N+1 to carry a stale `expectedGeneration` (request N hasn't returned yet, so
  // `sourceGenerationRef` isn't updated) and get silently rejected server-side — leaving the
  // filter input showing the latest value while the server filter stays stuck on an earlier
  // one. `pendingFilterRef` holds the most recent requested filter while one is in flight;
  // it's drained when the in-flight request completes.
  const filterInFlightRef = useRef(false);
  const pendingFilterRef = useRef<{ colFilters: Record<string, string>; gFilter: string } | null>(null);
  // The same single-flight guard for SORT dispatch. Two header clicks in quick
  // succession used to fire two IPCs: the second read `sourceGenerationRef`
  // before the first response had bumped it, the server saw a stale
  // `expectedGeneration` and returned without applying it, and the client
  // (whose token guard had already discarded the first response) accepted
  // that reply — the rows stayed sorted by the FIRST column while the header
  // showed the second (the "rapid column clicks" race, real MSSQL e2e). The
  // latest requested sort waits in `pendingSortRef` and is dispatched, with
  // the fresh generation, when the in-flight one settles.
  const sortInFlightRef = useRef(false);
  const pendingSortRef = useRef<{ state: { columnId: string; direction: 'asc' | 'desc' } | null } | null>(null);
  // Tracks the last filter values we've committed to dispatching. Used by the effect below to
  // detect whether a user-visible filter change actually requires an IPC. Declared here (above
  // handleWindowedFilterChange) because the callback writes to it after the coalesce check.
  const prevFilterRef = useRef({ columnFilters, globalFilter, showResultFilter });

  // Sync windowedTotalRowCount when remoteTotalRowCount changes (new query execution)
  useEffect(() => {
    if (remoteTotalRowCount != null) setWindowedTotalRowCount(remoteTotalRowCount);
  }, [remoteTotalRowCount]);

  // Reset sort/filter dataVersion and sourceGeneration on re-execution (cacheGeneration bump)
  useEffect(() => {
    if (cacheGeneration != null && cacheGeneration > 0) {
      setDataVersion(0);
      setSourceGeneration(0);
      sourceGenerationRef.current = 0;
      filterInFlightRef.current = false;
      pendingFilterRef.current = null;
    }
  }, [cacheGeneration]);

  // Reset all windowed state when switching result sets (cacheUri change)
  const prevCacheUriRef = useRef(cacheUri);
  useEffect(() => {
    if (prevCacheUriRef.current !== cacheUri) {
      prevCacheUriRef.current = cacheUri;
      setDataVersion(0);
      setSourceGeneration(0);
      sourceGenerationRef.current = 0;
      setWindowedTotalRowCount(remoteTotalRowCount ?? 0);
      setIsRemoteSorting(false);
      sortFilterTokenRef.current = 0;
      filterInFlightRef.current = false;
      pendingFilterRef.current = null;
    }
  }, [cacheUri, remoteTotalRowCount]);

  const effectiveTotalRowCount = isWindowed ? windowedTotalRowCount : (remoteTotalRowCount ?? 0);

  // Windowed sort: dispatch to Electron result source
  const handleWindowedSortChange = useCallback(
    async (newSortState: { columnId: string; direction: 'asc' | 'desc' } | null) => {
      if (!cacheUri) return;
      if (sortInFlightRef.current) {
        // Coalesce: keep only the latest intent while a request is in flight.
        pendingSortRef.current = { state: newSortState };
        return;
      }
      sortInFlightRef.current = true;
      const myToken = ++sortFilterTokenRef.current;
      const genBefore = cacheGenerationRef.current;
      const uriAtRequestTime = cacheUri;
      const expectedGen = sourceGenerationRef.current;
      setIsRemoteSorting(true);
      try {
        if (newSortState) {
          const resp = await sortResultCache({
            uri: cacheUri, columnId: newSortState.columnId, direction: newSortState.direction,
            expectedGeneration: expectedGen,
          });
          if (sortFilterTokenRef.current !== myToken) return;
          if (resp.notReady) {
            toast.default({ title: resp.message ?? 'Cannot sort while results are still loading.' });
            return;
          }
          if (cacheGenerationRef.current !== genBefore || cacheUriRef.current !== uriAtRequestTime) return;
          if (resp.generation < sourceGenerationRef.current) return;
          sourceGenerationRef.current = resp.generation;
          setWindowedTotalRowCount(resp.totalRowCount);
          setSourceGeneration(resp.generation);
        } else {
          const resp = await clearResultCacheSort({ uri: cacheUri, expectedGeneration: expectedGen });
          if (sortFilterTokenRef.current !== myToken) return;
          if (cacheGenerationRef.current !== genBefore || cacheUriRef.current !== uriAtRequestTime) return;
          if (resp.generation < sourceGenerationRef.current) return;
          sourceGenerationRef.current = resp.generation;
          setWindowedTotalRowCount(resp.totalRowCount);
          setSourceGeneration(resp.generation);
        }
        setDataVersion((v) => v + 1);
      } catch (err) {
        if (sortFilterTokenRef.current !== myToken) return;
        toast.error({ title: 'Sort failed', description: err instanceof Error ? err.message : String(err) });
      } finally {
        sortInFlightRef.current = false;
        if (sortFilterTokenRef.current === myToken) setIsRemoteSorting(false);
        // Drain the most recently queued sort, if any, against the generation
        // the just-completed response wrote; a URI switch discards it.
        const next = pendingSortRef.current;
        pendingSortRef.current = null;
        if (next && cacheUriRef.current === uriAtRequestTime) void handleWindowedSortChange(next.state);
      }
    },
    [cacheUri],
  );

  // Windowed filter: dispatch to Electron result source.
  //
  // Single-flight semantics: at most one filter IPC is in flight at any time. If the hook is
  // called again while a request is pending (user kept typing), the latest desired filter is
  // stored in `pendingFilterRef` and dispatched from the `finally` block once the current
  // request completes. This is required because the server rejects requests whose
  // `expectedGeneration` has fallen behind the source's generation, and rapid keystrokes all
  // read the same stale `sourceGenerationRef.current` before the first response has bumped it.
  const handleWindowedFilterChange = useCallback(
    async (colFilters: Record<string, string>, gFilter: string) => {
      if (!cacheUri) return;
      if (filterInFlightRef.current) {
        // Coalesce: keep only the latest intent while a request is in flight.
        pendingFilterRef.current = { colFilters, gFilter };
        return;
      }
      filterInFlightRef.current = true;
      // Mark this filter as the "last dispatched" intent so the triggering effect doesn't
      // redundantly call us again on the next render for the same values.
      prevFilterRef.current = { columnFilters: colFilters, globalFilter: gFilter, showResultFilter: true };
      const myToken = ++sortFilterTokenRef.current;
      const genBefore = cacheGenerationRef.current;
      const uriAtRequestTime = cacheUri;
      const expectedGen = sourceGenerationRef.current;
      setIsRemoteSorting(true);
      try {
        const resp = await filterResultCache({
          uri: cacheUri, columnFilters: colFilters, globalFilter: gFilter, columnKeys,
          expectedGeneration: expectedGen,
        });
        if (sortFilterTokenRef.current !== myToken) return;
        if (resp.notReady) {
          toast.default({ title: resp.message ?? 'Cannot filter while results are still loading.' });
          return;
        }
        if (cacheGenerationRef.current !== genBefore || cacheUriRef.current !== uriAtRequestTime) return;
        if (resp.generation < sourceGenerationRef.current) return;
        sourceGenerationRef.current = resp.generation;
        setWindowedTotalRowCount(resp.totalRowCount);
        setSourceGeneration(resp.generation);
        setDataVersion((v) => v + 1);
      } catch (err) {
        if (sortFilterTokenRef.current !== myToken) return;
        toast.error({ title: 'Filter failed', description: err instanceof Error ? err.message : String(err) });
      } finally {
        filterInFlightRef.current = false;
        if (sortFilterTokenRef.current === myToken) setIsRemoteSorting(false);
        // Drain the most recently queued filter, if any. This uses the up-to-date
        // `sourceGenerationRef` that the just-completed response wrote.
        const next = pendingFilterRef.current;
        if (next && cacheUriRef.current === uriAtRequestTime) {
          pendingFilterRef.current = null;
          void handleWindowedFilterChange(next.colFilters, next.gFilter);
        } else if (next) {
          // URI switched while we were in flight — discard the stale pending request.
          pendingFilterRef.current = null;
        }
      }
    },
    [cacheUri, columnKeys],
  );

  // Trigger windowed filter when filter state changes
  useEffect(() => {
    if (!isWindowed) return;
    const prev = prevFilterRef.current;

    // Filter UI toggled off → clear filter in Electron (keep sort)
    // Skip if the source does not support filtering (e.g., STS-backed MSSQL results)
    if (!showResultFilter && prev.showResultFilter) {
      prevFilterRef.current = { columnFilters, globalFilter, showResultFilter };
      if (capabilities?.canFilter === false) return;
      const myToken = ++sortFilterTokenRef.current;
      const genBefore = cacheGenerationRef.current;
      const uriAtRequestTime = cacheUri;
      const expectedGen = sourceGenerationRef.current;
      (async () => {
        setIsRemoteSorting(true);
        try {
          const resp = await clearResultCacheFilter({ uri: cacheUri!, expectedGeneration: expectedGen });
          if (sortFilterTokenRef.current !== myToken) return;
          if (cacheGenerationRef.current !== genBefore || cacheUriRef.current !== uriAtRequestTime) return;
          if (resp.generation < sourceGenerationRef.current) return;
          sourceGenerationRef.current = resp.generation;
          setWindowedTotalRowCount(resp.totalRowCount);
          setSourceGeneration(resp.generation);
          setDataVersion((v) => v + 1);
        } catch (err) {
          if (sortFilterTokenRef.current !== myToken) return;
          toast.error({ title: 'Clear filter failed', description: err instanceof Error ? err.message : String(err) });
        } finally {
          if (sortFilterTokenRef.current === myToken) setIsRemoteSorting(false);
        }
      })();
      return;
    }

    if (!showResultFilter) return;
    if (shallowEqualFilters(prev.columnFilters, columnFilters) && prev.globalFilter === globalFilter) return;
    // Don't write to prevFilterRef here — handleWindowedFilterChange writes it only once it
    // commits to dispatching, so coalesced/skipped calls don't lock out future changes.
    handleWindowedFilterChange(columnFilters, globalFilter);
  }, [isWindowed, showResultFilter, columnFilters, globalFilter, handleWindowedFilterChange, cacheUri, capabilities]);

  // Streaming CSV export from Electron cache. `resume` continues a previous
  // partial export by appending to its file from the last written row — the
  // partial toast's "Resume export" action re-enters this function via
  // `runCsvExportRef` (a ref, so the toast callback never goes stale).
  // `carriedSkippedRows`: skips from the PREVIOUS run(s) that sit below the
  // resume point — those rows are permanently missing from the file (resume
  // starts past them), so they must survive into the final report or a clean
  // resume would end in a plain success toast over a file with holes.
  type ExportResumeArgs = {
    fromRow: number;
    filePath: string;
    carriedSkippedRows?: number[];
    carriedSkipCauseClass?: SkipCauseClass;
  };
  const runCsvExportRef = useRef<(resume?: ExportResumeArgs) => Promise<GridExportOutcome>>(undefined);
  const runCsvExport = useCallback(async (resume?: ExportResumeArgs): Promise<GridExportOutcome> => {
    if (!cacheUri) return { success: false, error: 'This result set is not backed by a cache, so there is nothing to stream' };
    // Set in each branch below and returned after the `finally`. Every branch
    // already tells the USER what happened via a toast; this carries the same
    // answer back to an agent-initiated export.
    let outcome: GridExportOutcome = { success: false, error: 'Export did not complete' };
    setIsExporting(true);
    const startedAt = Date.now();
    let lastProgress = { current: resume?.fromRow ?? 0, total: 0 };
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    // Telemetry: smallest sub-batch size recovery mode reached (null = no
    // recovery needed). See csvExportTelemetry.ts for the property rules.
    let recoveryMinBatch: number | null = null;
    // S2 — cumulative unreadable-row skips shown in the progress text.
    // Monotonic within one export; a resume starts at the carried count (and
    // adds it to the per-run counts the electron side reports) so the toast
    // never under-reports rows already missing from the file.
    const carriedSkipCount = resume?.carriedSkippedRows?.length ?? 0;
    let skippedSoFar = carriedSkipCount;

    const toastId = toast.default({
      title: resume ? 'Resuming CSV export...' : 'Exporting CSV...',
      description: resume ? `Continuing from row ${resume.fromRow.toLocaleString()}` : 'Starting export',
      // A multi-million-row export can run for many minutes — the toast must
      // outlive any fixed duration and is dismissed explicitly below.
      persist: true,
      action: { label: 'Cancel', onClick: () => void cancelCsvExport(cacheUri) },
    });

    // If no progress event arrives for a while (a slow server-side batch),
    // say so — a frozen row count reads as "it hung", which is exactly the
    // wrong message while the export is patiently waiting on the server.
    const armStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        toast.update(toastId, {
          description: describeExportStall({
            ...lastProgress, elapsedMs: Date.now() - startedAt, skipped: skippedSoFar,
          }),
        });
      }, 10_000);
    };
    armStallTimer();

    const unsubscribe = onCsvExportProgress((progress) => {
      if (progress.uri !== cacheUri) return;
      // Electron reports per-run counts — add the skips carried over a resume.
      if (progress.skipped) {
        skippedSoFar = Math.max(skippedSoFar, carriedSkipCount + progress.skipped.count);
      }
      // Two producers emit `skipped`: the per-batch event from
      // `streamingCsvExport.ts` (a real cursor position — `current` is the
      // written high-water mark) and the per-row event from
      // `stsResultSource.ts`'s `onRowSkipped`, fired mid-window with
      // `current` = the START of the window being fetched. Both look alike, so
      // the row counter only ever moves forward — a mid-window skip event can
      // never walk it back to the start of the batch in flight.
      if (!progress.retry && !progress.recovery && progress.current >= lastProgress.current) {
        lastProgress = { current: progress.current, total: progress.total };
      }
      if (progress.recovery) {
        recoveryMinBatch = Math.min(recoveryMinBatch ?? Infinity, progress.recovery.batchSize);
      }
      const snapshot = { ...lastProgress, elapsedMs: Date.now() - startedAt, skipped: skippedSoFar };
      toast.update(toastId, {
        description: progress.recovery
          ? describeExportRecovery(snapshot, progress.recovery)
          : progress.retry
            ? describeExportRetry(snapshot, progress.retry)
            : describeExportProgress(snapshot),
      });
      armStallTimer();
    });
    try {
      const hasSpatial = !!nativeSpatialColumnIds && nativeSpatialColumnIds.size > 0;
      const result = await exportCacheToCsv({
        uri: cacheUri,
        // T5 — WKT export parity: only sent when this result set actually has
        // native-spatial columns, so the non-spatial request shape (the vast
        // majority of exports) is unchanged.
        spatialColumnIds: hasSpatial ? [...nativeSpatialColumnIds!] : undefined,
        engine: hasSpatial ? engine : undefined,
        filePath: resume?.filePath,
        resumeFromRow: resume?.fromRow,
      });
      toast.dismiss(toastId);
      const sharedProps = {
        mode: 'windowed-all',
        resumed: !!resume,
        recovered: recoveryMinBatch != null,
        // Which pipeline finished/failed the export. Omitted (not `undefined`)
        // for sources that don't report one — a missing property reads as
        // "not applicable" on the dashboard, an `undefined` one as a bug.
        ...(result.exportPath != null ? { exportPath: result.exportPath } : {}),
      };
      if (result.success) {
        // S2 — `rowCount` counts skipped positions too, so the data rows the
        // file actually holds are `rowCount - skippedRows.length`. A resumed
        // run merges in the skips carried from before its resume point —
        // those rows are just as missing from the finished file.
        const skippedRows = mergeSkippedRowIndexes(resume?.carriedSkippedRows, result.skippedRows);
        const skipCauseClass = mergeSkipCauseClass(resume?.carriedSkipCauseClass, result.skipCauseClass);
        outcome = {
          success: true,
          filePath: result.filePath,
          // Data rows actually IN the file: `rowCount` counts skipped
          // positions too (same arithmetic as the toast below).
          rowCount: result.rowCount - skippedRows.length,
        };
        window.electronAPI?.analytics
          ?.track('feature:export_results', {
            format: 'csv', ...sharedProps,
            // Boolean + low-cardinality buckets only — row indexes are
            // high-cardinality and never leave the toast. `skipCauseClass`
            // 'unknown' flags an error class we did not anticipate.
            skipped: skippedRows.length > 0,
            ...(skippedRows.length > 0
              ? { skippedBucket: bucketSkippedRows(skippedRows.length) }
              : {}),
            ...(skippedRows.length > 0 && skipCauseClass != null ? { skipCauseClass } : {}),
          })
          .catch(() => {});
        if (skippedRows.length > 0) {
          // Cache URIs are per tab/result-set INDEX and come back after a
          // re-run or a switch-away-and-back (`result-cache://<tab>/rs/0`
          // again) — but the generation bumps, and the old row indexes point
          // into a buffer that no longer exists. Guard on both.
          const uriAtExport = cacheUri;
          const generationAtExport = cacheGenerationRef.current;
          const stillCurrent = () =>
            isMountedRef.current &&
            cacheUriRef.current === uriAtExport &&
            cacheGenerationRef.current === generationAtExport;
          // Raise the durable "Skipped rows" bottom tab first — the toast can
          // be dismissed, the tab lives as long as the result set. Skipped when
          // the export outlived its result set (switch/re-run/unmount): the
          // indexes point into a buffer that is gone.
          if (stillCurrent()) onExportSkippedRowsRef.current?.([...skippedRows]);
          // Toast actions are offered only when the caller can actually serve
          // them: a button whose only outcome is "that view is gone" is worse
          // than no button. One skipped row → a direct labeled jump; several →
          // "Review skipped rows", which lands on the tab raised above.
          const canJump = scrollToRowRef.current != null;
          const canReview = onExportSkippedRowsRef.current != null && onReviewSkippedRowsRef.current != null;
          // The toast persists: by the time an action is clicked the tab may be
          // closed or showing a different result set, where a row index from
          // this export means nothing. Say so rather than acting on some
          // unrelated grid — or doing nothing at all.
          const reportResultSetGone = () => {
            toast.default({
              title: 'This result set is no longer open',
              description: 'Re-run the query to jump to the skipped row.',
            });
          };
          showExportSkippedRowsToast(
            result.filePath, result.rowCount - skippedRows.length, result.rowCount,
            skippedRows, 'query-editor',
            !canJump ? undefined : (rowIndex) => {
              const jump = scrollToRowRef.current;
              if (!jump || !stillCurrent()) {
                reportResultSetGone();
                return;
              }
              jump(rowIndex);
            },
            !canReview ? undefined : () => {
              const review = onReviewSkippedRowsRef.current;
              if (!review || !stillCurrent()) {
                reportResultSetGone();
                return;
              }
              review();
            },
          );
        } else {
          showExportSuccessToast(result.filePath, 'query-editor', `${result.rowCount.toLocaleString()} rows exported`);
        }
      } else if (result.cancelled) {
        toast.default({ title: 'Export cancelled' });
        outcome = { success: false, error: 'Export cancelled', cancelled: true };
      } else if (result.partialFilePath != null && result.partialRowCount != null) {
        const partialSkipCauseClass = mergeSkipCauseClass(
          resume?.carriedSkipCauseClass, result.skipCauseClass,
        );
        window.electronAPI?.analytics
          ?.track('feature:export_csv_partial_kept', {
            ...sharedProps,
            failureClass: classifyExportFailure(result.error),
            sizeBucket: bucketRowCount(lastProgress.total),
            wideTypes: hasWideValueColumns ?? false,
            ...(partialSkipCauseClass != null ? { skipCauseClass: partialSkipCauseClass } : {}),
          })
          .catch(() => {});
        // Skips below the new high-water mark are permanently missing (the
        // next resume starts past them) — carry them so the eventual success
        // report covers the whole resume chain. Skips at/above it will be
        // re-attempted by the resume and re-reported only if they fail again.
        const partial = {
          fromRow: result.partialRowCount,
          filePath: result.partialFilePath,
          carriedSkippedRows: mergeSkippedRowIndexes(
            resume?.carriedSkippedRows, result.skippedRows,
          ).filter((i) => i < result.partialRowCount!),
          carriedSkipCauseClass: partialSkipCauseClass,
        };
        outcome = {
          success: false,
          error:
            `${result.error} — the partial file was KEPT at ${partial.filePath} ` +
            `with ${partial.fromRow.toLocaleString()} rows. The user can resume it from the toast.`,
        };
        showExportPartialKeptToast(
          partial.filePath, partial.fromRow, result.error, 'query-editor',
          () => {
            window.electronAPI?.analytics
              ?.track('feature:export_csv_resume', { grid: 'query-editor' })
              .catch(() => {});
            void runCsvExportRef.current?.(partial);
          },
        );
      } else {
        toast.error({ title: 'Export failed', description: result.error });
        outcome = { success: false, error: result.error ?? 'Export failed' };
      }
    } finally {
      if (stallTimer) clearTimeout(stallTimer);
      unsubscribe();
      setIsExporting(false);
    }
    return outcome;
  }, [cacheUri, nativeSpatialColumnIds, engine, hasWideValueColumns]);
  useEffect(() => {
    runCsvExportRef.current = runCsvExport;
  }, [runCsvExport]);

  const handleExportAllCsv = useCallback(() => runCsvExport(), [runCsvExport]);

  return {
    windowedTotalRowCount,
    effectiveTotalRowCount,
    dataVersion,
    isRemoteSorting,
    handleWindowedSortChange,
    handleExportAllCsv,
    isExporting,
    capabilities,
    sourceGeneration,
  };
}
